import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toNumber } from 'ng-zorro-antd/core/util';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HttpRequestService, LocalStorageService } from 'src/app/core/services';
import { RejectionReasonComponent } from 'src/app/shared/components/rejection-reason/rejection-reason.component';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-job-alerts',
  templateUrl: './job-alerts.component.html',
  styleUrls: ['./job-alerts.component.scss'],
})
export class JobAlertsComponent implements OnInit {
  /* ── Data ── */
  allJobAlerts: any[] = [];
  allApprovedJobAlerts: any[] = [];
  totalDataCount = 0;
  totalApprovedDataCount = 0;

  /* ── Pagination ── */
  pageSize = 12;
  pageIndex = 1;
  skipIndex = 0;

  /* ── UI state ── */
  loading = false;
  index = 0;
  userRole = '';

  /* ── Filters ── */
  search = '';
  selectedCategory: string | null = null;
  selectedJobType: string | null = null;
  startDate: any = null;
  endDate: any = null;
  dateRange: any = null;

  /* ── Category dropdown ── */
  listOfCategories: any[] = [];
  totalCategoryCount = 0;
  categoryPageIndex = 1;
  categoryPageSize = 10;
  categorySearchStr = '';
  searchCategorySubject: Subject<string> = new Subject<string>();

  /* ── Search debounce ── */
  searchJobAlertSubject: Subject<string> = new Subject<string>();

  /* ── Static options ── */
  jobTypes = [
    'Central Govt',
    'State Govt',
    'Bank',
    'Railway',
    'Defence',
    'Teaching',
    'PSC',
    'Other',
  ];

  publishedStatusOptions = ['published', 'unpublished'];

  jobTypeColorMap: Record<string, string> = {
    'Central Govt': 'blue',
    'State Govt': 'geekblue',
    Bank: 'gold',
    Railway: 'cyan',
    Defence: 'red',
    Teaching: 'green',
    PSC: 'purple',
    Other: 'default',
  };

  constructor(
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
    private localStorageService: LocalStorageService,
    private modalService: NzModalService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {
    const snapIndex = toNumber(
      this.activatedRoute.snapshot.queryParams['index'],
    );
    this.userRole = this.localStorageService.getItem('user').role;
    if (snapIndex) {
      this.changeIndex(snapIndex);
    }
  }

  ngOnInit(): void {
    /* Search debounce */
    this.searchJobAlertSubject
      .pipe(debounceTime(1000))
      .subscribe((val: string) => {
        this.search = val;
        this.resetAndFetch();
      });

    /* Category search debounce */
    this.searchCategorySubject
      .pipe(debounceTime(600))
      .subscribe((val: string) => {
        this.categorySearchStr = val;
        this.listOfCategories = [];
        this.categoryPageIndex = 1;
        this.getAllCategories();
      });

    this.getAllCategories();
    this.getAllJobAlerts(this.index);
  }

  /* ──────────────────── Tab / Index ──────────────────── */

  changeIndex(data: any): void {
    this.index = data;
    this.router.navigate([], { queryParams: { index: data } });
    this.pageSize = 12;
    this.pageIndex = 1;
    this.skipIndex = 0;
    this.allJobAlerts = [];
    this.allApprovedJobAlerts = [];
    this.getAllJobAlerts(this.index);
  }

  /* ──────────────────── Fetch Job Alerts ──────────────────── */

  getAllJobAlerts(index: number = 0, skip = 0, sortBy?: any): void {
    const params: any = { skip: this.skipIndex, limit: this.pageSize };

    if (this.search) params.search = this.search;
    if (this.selectedCategory) params.jobCategory = this.selectedCategory;
    if (this.selectedJobType) params.jobType = this.selectedJobType;
    if (sortBy) params.sortBy = JSON.stringify(sortBy);

    if (this.startDate && this.endDate) {
      params.startDate = this.startDate;
      params.endDate = this.endDate;
    }

    /* Approval status filter by tab + role */
    if (index === 0) {
      params.approveStatus = 'approved';
    } else if (index === 1 && this.userRole === 'admin') {
      params.approveStatus = 'pending';
    } else if (index === 1 && this.userRole === 'subAdmin') {
      params.approveStatus = ['pending', 'rejected'];
    }

    this.loading = true;
    this.httpRequestService.request('get', 'job-alerts', params).subscribe(
      (result: any) => {
        this.loading = false;
        if (index === 0) {
          this.allApprovedJobAlerts = result.data;
          this.totalApprovedDataCount = result.totalCount;
        } else {
          this.allJobAlerts = result.data;
          this.totalDataCount = result.totalCount;
        }
      },
      () => {
        this.loading = false;
      },
    );
  }

  /* ──────────────────── Fetch Categories ──────────────────── */

  getAllCategories(skip = 0): void {
    const params: any = { skip, limit: this.categoryPageSize };
    if (this.categorySearchStr) params.search = this.categorySearchStr;
    this.httpRequestService.request('get', 'exam-categories', params).subscribe(
      (result: any) => {
        this.listOfCategories.push(...result.data);
        this.totalCategoryCount = result.totalCount;
      },
      () => {},
    );
  }

  onCategorySearch(event: string): void {
    this.searchCategorySubject.next(event);
  }

  loadMoreCategories(): void {
    if (this.totalCategoryCount !== this.listOfCategories.length) {
      this.categoryPageIndex++;
      this.getAllCategories(
        this.categoryPageSize * (this.categoryPageIndex - 1),
      );
    }
  }

  /* ──────────────────── Filter handlers ──────────────────── */

  onCategoryChange(value: string | null): void {
    this.selectedCategory = value;
    this.resetAndFetch();
  }

  onJobTypeChange(value: string | null): void {
    this.selectedJobType = value;
    this.resetAndFetch();
  }

  onChangeDateRange(date: any): void {
    this.startDate = date[0];
    this.endDate = date[1];
    this.resetAndFetch();
  }

  searchJobAlerts(event: any): void {
    this.searchJobAlertSubject.next(event);
  }

  private resetAndFetch(): void {
    this.pageIndex = 1;
    this.skipIndex = 0;
    this.allJobAlerts = [];
    this.allApprovedJobAlerts = [];
    this.getAllJobAlerts(this.index);
  }

  /* ──────────────────── Pagination ──────────────────── */

  onQueryParamsChange(params: NzTableQueryParams): void {
    this.allApprovedJobAlerts = [];
    this.allJobAlerts = [];
    const { pageSize, pageIndex, sort } = params;
    this.pageSize = pageSize;
    this.pageIndex = pageIndex;
    this.skipIndex = pageSize * (pageIndex - 1);
    const currentSort = sort.find((s) => s.value !== null);
    const sortBy = currentSort
      ? { [currentSort.key]: currentSort.value === 'ascend' ? 1 : -1 }
      : null;
    this.getAllJobAlerts(this.index, this.skipIndex, sortBy);
  }

  /* ──────────────────── Status Actions ──────────────────── */

  updateStatus(id: string, status: boolean): void {
    this.httpRequestService
      .request('put', `job-alerts/active-inactive/${id}`, { status: !status })
      .subscribe(
        () => {
          this.notificationService.success('', 'Status Updated Successfully');
          this.getAllJobAlerts(this.index);
        },
        () => {},
      );
  }

  updateApproveStatus(id: string, approveStatus: string): void {
    this.httpRequestService
      .request('put', `job-alerts/approved-rejected/${id}`, { approveStatus })
      .subscribe(
        () => {
          this.notificationService.success('', 'Status Updated Successfully');
          this.getAllJobAlerts(this.index);
        },
        () => {},
      );
  }

  updatePublishedStatus(id: string, publishedStatus: string): void {
    if (!publishedStatus) {
      return;
    }

    this.httpRequestService
      .request('put', `job-alerts/published-status/${id}`, {
        publishedStatus,
      })
      .subscribe(
        () => {
          this.notificationService.success(
            '',
            'Published Status Updated Successfully',
          );
          this.getAllJobAlerts(this.index);
        },
        () => {},
      );
  }

  deleteJobAlert(id: string): void {
    this.httpRequestService.request('delete', `job-alerts/${id}`).subscribe(
      () => {
        this.notificationService.success('', 'Job Alert Deleted Successfully');
        this.getAllJobAlerts(this.index);
      },
      () => {},
    );
  }

  openRejectionReason(id: string): void {
    const modal = this.modalService.create({
      nzTitle: 'Add A Reason',
      nzContent: RejectionReasonComponent,
      nzWidth: '800px',
      nzFooter: null,
      nzComponentParams: {
        updateId: id,
        component: 'job-alerts',
      },
      nzCancelText: null,
      nzOkText: null,
    });
    modal.afterClose.subscribe(() => this.getAllJobAlerts(this.index));
  }

  /* ──────────────────── Helpers ──────────────────── */

  getJobTypeColor(jobType: string): string {
    return this.jobTypeColorMap[jobType] ?? 'default';
  }

  /* ──────────────────── Drag & Drop ──────────────────── */

  onDropApproved(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(
      this.allApprovedJobAlerts,
      event.previousIndex,
      event.currentIndex,
    );

    const reorderedPayload = this.allApprovedJobAlerts.map((item, index) => ({
      id: item._id,
      order: index + this.skipIndex,
    }));

    this.httpRequestService
      .request('put', 'job-alerts/reorder', {
        reorderedItems: reorderedPayload,
      })
      .subscribe({
        next: () => {
          this.notificationService.success('', 'Order Updated Successfully');
          this.getAllJobAlerts(this.index);
        },
        error: () => {
          this.getAllJobAlerts(this.index);
        },
      });
  }

  onDropUnapproved(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(this.allJobAlerts, event.previousIndex, event.currentIndex);

    const reorderedPayload = this.allJobAlerts.map((item, index) => ({
      id: item._id,
      order: index + this.skipIndex,
    }));

    this.httpRequestService
      .request('put', 'job-alerts/reorder', {
        reorderedItems: reorderedPayload,
      })
      .subscribe({
        next: () => {
          this.notificationService.success('', 'Order Updated Successfully');
        },
        error: () => {
          this.getAllJobAlerts(this.index);
        },
      });
  }
}
